/**
 * Plan 21 — Cafeteria Mode REST client.
 *
 * Customer-facing /cafeteria page (CAFETERIA_WORKER + paired kiosks).
 * Per the apps/ordo "REST for client, GraphQL for AI tool" policy,
 * this file is REST-only via TanStack Query. The AI-tool GraphQL
 * surface lives in `packages/graphql/src/types/cafeteria.ts` (already
 * shipped with the schema manager).
 *
 * Hooks:
 *
 *   - `useUnservedBucketsQuery(args)` — list buckets to serve.
 *   - `useServeAnonymousMutation()` — UNASSIGNED → SERVED with no student.
 *   - `useServeToStudentMutation()` — UNASSIGNED → ASSIGNED → SERVED.
 *   - `useVoidUnassignedMutation()` — UNASSIGNED → VOIDED.
 *   - `useAllergenCheckMutation()` — pre-scan allergen lookup.
 *   - `useCommitServedComponentsMutation()` — record OVS subset.
 *
 * Wire shape mirrors `apps/ordo/server/src/routes/cafeteria.ts` — every
 * field on the server-side serializer is exposed here verbatim.
 *
 * Offline behavior (Plan 23): the three serve-state-changing
 * mutations (`serveAnonymous`, `serveToStudent`, `voidUnassigned`)
 * are queue-on-failure. When the tablet is offline OR the fetch
 * fails with a TypeError, the mutation enqueues a SyncQueue row and
 * resolves with a synthesized response carrying
 * `status: 'OFFLINE_QUEUED'`. The page surfaces this as a "queued"
 * toast so workers can keep serving. Plan 24 owns the replay loop.
 * Allergen-check + commit-served-components remain online-only —
 * they require server-side data the cache can't synthesize.
 */

import type { DayOfWeek, OrderDeadlineMode } from '@ordo/recurrence'
import type { QueryClient } from '@tanstack/react-query'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------
import type { Mealtime } from '@/lib/mealtime'

import { enqueueMutation, generateQueueId } from '../cafeteria-db/sync-queue'
import type { TemperatureReadingWire } from './cafeteria-temperatures'
import { ApiError, apiFetch } from './http'
export type { Mealtime }
export type CafeteriaTimelineStatus = 'UPCOMING' | 'NOW' | 'OVERDUE' | 'COMPLETE'

/** A serve session's progress-based status (no clock / no deadlines). */
export type ServeSessionStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETE'

export interface UnservedBucketWire {
  menuItemId: string
  menuItemName: string
  gradeRangeId: string | null
  gradeRangeName: string | null
  /**
   * PROD-1122 — band identity + bounds for the shared pool-compliance
   * rule (`poolComplianceTier` in @ordo/constants). Optional: a stale
   * server omits them and banded buckets fail closed on display.
   */
  gradeRangeCode?: string | null
  gradeRangeMinGrade?: string | null
  gradeRangeMaxGrade?: string | null
  /** The bucket's group axis (HOMEROOM etc.); AGE_GROUP follows the band rule. */
  groupDimension?: string | null
  groupValue?: string | null
  /** Homeroom/group display name for group-bucketed items. */
  rosterName: string | null
  /** SUM of quantities — counts MEALS, not rows (PROD-1122 fix). */
  count: number
  sampleIds: string[]
}

export interface UnservedBucketsResponse {
  buckets: UnservedBucketWire[]
}

export interface OrderItemWire {
  id: string
  status: string
  assignedToUserId: string | null
  servedAt: string | null
  servedByUserId: string | null
  billedToAccountId: string | null
  menuItemId: string | null
  batchId: string | null
}

export interface ServeResponse {
  orderItem: OrderItemWire
  warnings: string[]
  locationId: string
}

export interface AllergenCheckResponse {
  match: boolean
  componentId: string | null
  reason: string | null
  alternateComponentId: string | null
}

export interface CommitServedComponentsResponse {
  orderItemId: string
  acceptedComponentIds: string[]
  recorded: boolean
}

/** One campus milk SKU option for the serve-confirm milk radio (PROD-1121). */
export interface CafeteriaMilkSkuWire {
  componentId: string
  milkOptionCode: string | null
  label: string
}

/**
 * The student dimension a campus groups by (`EnrollmentFactDimension` in
 * `@ordo/schema`). `NONE` = one undivided list, never a bucket.
 */
export type ServeGroupDimension = 'AGE_GROUP' | 'GRADE' | 'TEACHER' | 'ROOM' | 'HOMEROOM' | 'LUNCH_PERIOD' | 'NONE'

/**
 * The campus's effective enrollment-grouping policy (`resolveGroupingPolicy`
 * in `@ordo/schema`). `serveGroupBy` names the dimension every `groupLabel`
 * on this payload was resolved under, which is what lets the serve filters
 * file a bucket on the right axis instead of guessing from its text.
 */
export interface CafeteriaGroupingPolicyWire {
  serveGroupBy: ServeGroupDimension
  /** False = the campus is unconfigured and running the whole-school default. */
  configured: boolean
}

export interface CafeteriaStaffLocationWire {
  locationId: string
  locationName: string
  schoolName: string
  organizationId: string
  timeZone: string
  orderMethods: string[]
  serviceStyle: 'OVS' | 'SS' | null
  productionRecordsEnabled?: boolean
  /**
   * PROD-1168 — whether THIS user may fill and sign the campus's production
   * records. School staff always may; a chef with a cafeteria grant may only
   * when the Ordo team issued it at the serve + production records level.
   * Populated by the worker-locations list (`GET /api/cafeteria/locations`);
   * absent on older servers, which consumers must treat as "allowed".
   */
  productionRecordsAccess?: boolean
  /**
   * Optional like the fields below it: only the service-session payloads
   * populate it (the worker-locations list omits it), so consumers must
   * handle its absence.
   */
  groupingPolicy?: CafeteriaGroupingPolicyWire
  /**
   * PROD-1121 — milk SKUs for the serve-confirm milk radio. Populated only
   * by the service-session payloads, and only where production records are
   * enabled; absent/empty = no radio.
   */
  milkSkus?: CafeteriaMilkSkuWire[]
  /**
   * Server-resolved effective POS (cafeteria mode AND POS, org default +
   * campus override — PROD-227). Presentation-only: gates the serve page's
   * "Sell items" entry; every `/api/cafeteria/pos/*` endpoint re-gates
   * server-side. Optional like `productionRecordsEnabled` — only the
   * service-session payloads populate it.
   */
  posEnabled?: boolean
  /**
   * Order-deadline policy — only the service-session endpoints populate these
   * (the worker-locations list omits them), so every field is optional and
   * consumers must handle their absence. Read them through
   * `cafeteriaDeadlinePolicy` in `@/lib/cafeteria/order-window`, which returns
   * null unless the whole policy arrived.
   */
  orderDeadlineMode?: OrderDeadlineMode
  orderDeadlineDayOfWeek?: DayOfWeek | null
  orderDeadlineWeeksBefore?: number
  orderDeadlineTime?: string
  orderDeadlineRelativeDays?: number | null
}

export type ServeMode = 'STUDENT_CARDS' | 'QUANTITY_TABLE' | 'MIXED' | 'EMPTY'

export interface ServiceComponentTotalWire {
  componentId: string
  componentName: string
  componentKind: string | null
  planned: number
  served: number
}

export interface CafeteriaLocationsResponse {
  locations: CafeteriaStaffLocationWire[]
}

export interface ServiceSessionListItemWire {
  mealtime: Mealtime
  label: string
  serviceStarts: string
  serviceEnds: string
  totalItems: number
  remainingItems: number
  totalStudents: number
  remainingStudents: number
  status: ServeSessionStatus
}

export interface ServiceSessionsResponse {
  sessions: ServiceSessionListItemWire[]
}

export interface ServiceComponentWire {
  slotKey: string
  componentId: string
  componentName: string
  componentKind: string
  accepted: boolean
  offered: boolean
  /**
   * PROD-1121 — the observed pick when it differs from the offered
   * component (today: the milk slot's carton). Null = as-offered or
   * never recorded.
   */
  servedComponentId: string | null
  imageUri: string | null
  servingSize: string | null
}

export interface ServiceDeliverableUnitWire {
  deliverableId: string | null
  /**
   * Source component identity — always the ORIGINAL catalog component even
   * when a chef swap renamed the unit (server rule, PROD-225). Null for
   * pick-less fallback packages.
   */
  componentId: string | null
  label: string
  componentName: string | null
  componentKind: string | null
  servingSize: string | null
  disherSize: string | null
  quantityLabel: string | null
  temperatureClass: 'HOT' | 'CHILLED' | 'AMBIENT' | null
  isBulk: boolean
}

export interface ServiceDeliverableDisplayGroupWire {
  id: string
  label: string
  source: 'TEMPLATE_BUCKET' | 'DELIVERABLE' | 'PRODUCT'
  /**
   * Bucket code (`MAIN`, `VEG`, `OPTION_2`, …) on a `TEMPLATE_BUCKET` group,
   * null otherwise — drives the serve card's meal-line color (PROD-1304).
   * Optional so payloads cached before the field shipped stay renderable.
   */
  bucketCode?: string | null
  units: ServiceDeliverableUnitWire[]
}

export interface ServiceMenuProductWire {
  productKind: 'BUNDLE' | 'COMPONENT'
  productName: string
  imageUri: string | null
  deliverableUnits: ServiceDeliverableUnitWire[]
  displayGroups: ServiceDeliverableDisplayGroupWire[]
}

export interface StudentAllergenWire {
  id: string
  label: string
}

export interface ServiceOrderItemWire {
  orderItemId: string
  menuItemId: string
  menuItemName: string
  imageUri: string | null
  servingSize: string | null
  /** Servings this row represents (forecast bucket count, e.g. 10; parent = 1). */
  quantity: number
  menuProduct: ServiceMenuProductWire | null
  source: 'SCHOOL_PREORDER' | 'PARENT_ORDER'
  originator: string
  status: string
  mealPlanId: string | null
  mealPlanName: string | null
  gradeRangeId: string | null
  gradeRangeName: string | null
  /** Homeroom/group display name (line tuple / Enrollment facts). */
  rosterName: string | null
  /**
   * The serve bucket this item belongs to (line tuple for school rows,
   * Enrollment facts for student rows). Group and filter by this first;
   * `gradeRangeName`/`rosterName` are display fallbacks.
   */
  groupLabel: string | null
  studentUserId: string | null
  studentName: string | null
  /** Active student allergy tags; absent on older cached responses. */
  studentAllergens?: StudentAllergenWire[]
  /**
   * The student's name is written family name first (PROD-1107). `studentName`
   * already reflects it — this rides along so the client can tell which END of
   * that string is the family name when sorting or greeting.
   */
  studentFamilyNameFirst: boolean
  /** The student's scannable Ordo student ID (e.g. `LIN-X7K9P3`). */
  ordoStudentId: string | null
  /** The school/SIS-provided student ID from the roster import. */
  localStudentId: string | null
  /**
   * PROD-1278 — the student's own grade label ("Grade 9") for the serve
   * screen's rows and header. Not the portion tier (`gradeRangeName`) and
   * not the serve bucket (`groupLabel`). Optional: an older server omits it.
   */
  gradeLabel?: string | null
  /**
   * PROD-1439 — the diner is campus staff ordering for themselves (a teacher
   * self-diner). Staff have no serve bucket, so the printed roster files them
   * under "Staff". Optional: an older server omits it.
   */
  isStaffDiner?: boolean
  servedAt: string | null
  components: ServiceComponentWire[]
}

export interface BulkServiceGuideWire {
  id: string
  menuProductName: string
  componentName: string
  componentKind: string | null
  servingSize: string | null
  disherSize: string | null
  plannedQuantity: number
  unitLabel: string
  temperatureClass: 'HOT' | 'CHILLED' | 'AMBIENT' | null
}

export interface ServeDeliverableBucketWire {
  bundleDeliverableId: string | null
  menuItemId: string
  tierGradeRangeId: string | null
  planned: number
}

/** One school-serve row per package × grade group (identical packages merged). */
export interface ServeDeliverableRowWire {
  deliverableKey: string
  label: string
  temperatureClass: 'HOT' | 'CHILLED' | 'AMBIENT' | null
  isBulk: boolean
  portionLabel: string | null
  /** Serving utensil (e.g. "#8 disher"); shown beside the portion. */
  disherSize: string | null
  gradeGroupId: string | null
  gradeGroupName: string | null
  /** Homeroom/group display name — non-AGE_GROUP dimensions (gradeGroupName is null there). */
  rosterName: string | null
  /** The stored `(groupDimension, groupValue)` group this row covers + its display label. */
  groupDimension: string | null
  groupValue: string | null
  groupLabel: string | null
  planned: number
  served: number
  buckets: ServeDeliverableBucketWire[]
}

export interface CafeteriaServiceSessionResponse {
  location: CafeteriaStaffLocationWire
  mealtime: Mealtime
  serviceDate: string
  serviceStyle: 'OVS' | 'SS' | null
  orderMethods: string[]
  summary: {
    status: ServeSessionStatus
    serviceStarts: string
    serviceEnds: string
    totalItems: number
    servedItems: number
    remainingItems: number
  }
  serveMode: ServeMode
  instructions: string | null
  items: ServiceOrderItemWire[]
  deliverableRows: ServeDeliverableRowWire[]
  componentTotals: ServiceComponentTotalWire[]
  bulkServiceGuides: BulkServiceGuideWire[]
}

export interface CompleteServiceInput {
  schoolLocationId: string
  serviceDate: string
  mealtime: Mealtime
  /** School-serve per-deliverable tally to officialize into the production record. */
  servedDeliverables?: Array<{
    bundleDeliverableId: string | null
    menuItemId: string | null
    /** Portion tier (the PR bucket tier key). */
    gradeRangeId: string | null
    /** `(groupDimension, groupValue)` — the PR bucket identity (both or neither). */
    groupDimension: string | null
    groupValue: string | null
    servedQty: number
  }>
}

export interface CompleteServiceResponse {
  completed: boolean
  batchesUpdated: number
}

export interface CafeteriaStudentWire {
  studentUserId: string
  studentName: string
  /**
   * The student's name is written family name first (PROD-1107). `studentName`
   * already reflects it — this rides along so the client can tell which END of
   * that string is the family name when sorting or greeting.
   */
  studentFamilyNameFirst: boolean
  ordoStudentId: string | null
  /** Homeroom name from the student's Enrollment facts. */
  rosterName: string | null
  gradeLevel: string | null
  mealPlanId: string | null
  mealPlanName: string | null
}

export interface CafeteriaStudentsResponse {
  students: CafeteriaStudentWire[]
}

export interface ServiceDayGateBlockerWire {
  serviceDate: string
  deliveriesRemaining: number
  servicesRemaining: number
  unservedItems: number
}

export interface ServiceDayGateResponse {
  requestedServiceDate: string
  activeServiceDate: string
  blocked: boolean
  blocker: ServiceDayGateBlockerWire | null
}

export interface CafeteriaDeliveryLineWire {
  shipmentLineId: string
  label: string
  ageRangeNames: string[]
  plannedQuantity: number
  receivedQuantity: number | null
  /**
   * The cafeteria's own Delivered count for this line's inventory row, fanned
   * onto the line by the server (PROD-1278): a prefill hint for the Confirm
   * deliveries step; null when the row was not edited, the line is already
   * received, or the line is weekly milk.
   */
  cafeteriaCountedQuantity: number | null
  /**
   * The cafeteria's own receipt temperature for this line's inventory row,
   * logged on the Temperature logs page: the Transport logs step's pre-fill,
   * ahead of the chef's readings. Null when nothing was logged, the line is
   * already received, or the line is weekly milk (one reading per drop).
   */
  cafeteriaTemperature: TemperatureReadingWire | null
  unitLabel: string
  componentId: string | null
  componentName: string | null
  componentKind: string | null
  /** `Component.milkOptionCode` — non-null marks a milk SKU line. */
  milkOptionCode: string | null
  deliverableName: string | null
  temperatureClass: 'HOT' | 'CHILLED' | 'AMBIENT' | null
  deliveryTemperatureF: number | null
  facilityTemperatureF: number | null
  deliveryTemperatureSource: 'MANUAL' | 'PHOTO' | null
  facilityTemperatureSource: 'MANUAL' | 'PHOTO' | null
  deliveryTemperatureConfidence: 'high' | 'low' | null
  facilityTemperatureConfidence: 'high' | 'low' | null
}

export interface CafeteriaDeliveryWire {
  shipmentId: string
  vendorLocationId: string
  vendorLocationName: string
  schoolLocationId: string
  /** `Location.milkDeliveryDayOfWeek` — non-null means this campus takes milk
   * on a fixed weekly delivery day (milk lines fan into weekly shipments). */
  schoolMilkDeliveryDayOfWeek: string | null
  serviceDate: string
  deliveryDate: string
  mealtime: Mealtime
  state: string
  status: 'NEEDS_RECEIPT' | 'RECEIVED' | 'VOIDED'
  timelineStatus: CafeteriaTimelineStatus
  deliveryWindow: string | null
  totalServings: number
  receivedAt: string | null
  receivedByUserId: string | null
  receiverName: string | null
  tempAtReceiptF: number | null
  notes: string | null
  taskId: string | null
  lines: CafeteriaDeliveryLineWire[]
}

export interface CafeteriaDeliveriesResponse {
  deliveries: CafeteriaDeliveryWire[]
}

export interface ReceiveDeliveryInput {
  schoolLocationId: string
  shipmentId: string
  arrivedAt: string
  receiverName: string
  tempAtReceiptF?: number | null
  notes?: string | null
  lineQuantities: Array<{ shipmentLineId: string; receivedQuantity: number }>
  lineTemperatureLogs: Array<{
    shipmentLineId: string
    deliveryTemperatureF: number
    facilityTemperatureF?: number | null
    deliverySource?: 'MANUAL' | 'PHOTO'
    facilitySource?: 'MANUAL' | 'PHOTO'
    deliveryConfidence?: 'high' | 'low' | null
    facilityConfidence?: 'high' | 'low' | null
  }>
}

export interface ReceiveDeliveryResponse {
  delivery: CafeteriaDeliveryWire
  durableRowsWritten: number
  taskCompleted: boolean
}

// ---------------------------------------------------------------------------
// Input shapes
// ---------------------------------------------------------------------------

export interface UnservedBucketsInput {
  schoolLocationId: string
  serviceDate: string // ISO yyyy-mm-dd or full ISO; server parses with `new Date`
  mealtime: Mealtime
}

/**
 * Plan 24 — every serve / void mutation carries a client-generated
 * `idempotencyKey`. The server's CafeteriaManager dedupes on this key
 * across replays so a row that fires once foreground + once after
 * reconnection collapses to a single serve. The hook layer below
 * auto-generates the key when the caller omits it (the foreground
 * path); the replay manager passes the queue row id as the key on
 * retries so the foreground enqueue + the replay use the same value.
 */
export interface ServeAnonymousInput {
  orderItemId: string
  locationId: string
  notes?: string
  /**
   * PROD-1121 — serve-confirm dialog picks. The mealtime-completion commit
   * serves student items through this mutation, so the dialog's component
   * checklist and milk pick ride here. `servedMilkComponentId` is
   * three-state: leave the KEY OUT entirely when the milk radio never
   * rendered; send an explicit null for "No milk"; send the SKU Component
   * id for a taken carton.
   */
  acceptedComponentIds?: string[]
  servedMilkComponentId?: string | null
  /**
   * PROD-1278 — the pre-selected milk default was left untouched. Send only
   * alongside a milk key; the server records it on the history row.
   */
  servedMilkDefaulted?: boolean
  idempotencyKey?: string
}

export interface ServeToStudentInput {
  orderItemId: string
  studentUserId: string
  locationId: string
  /** Staff intent only — the server decides whether money moves (PROD-227 PR7). */
  rerouteBillingToFamily?: boolean
  notes?: string
  acceptedComponentIds?: string[]
  /** See `ServeAnonymousInput.servedMilkComponentId` (PROD-1121). */
  servedMilkComponentId?: string | null
  idempotencyKey?: string
}

export interface VoidUnassignedInput {
  orderItemId: string
  locationId: string
  reason: string
  idempotencyKey?: string
}

export interface AllergenCheckInput {
  orderItemId: string
  studentUserId: string
}

export interface CommitServedComponentsInput {
  orderItemId: string
  locationId: string
  acceptedComponentIds: string[]
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

export async function fetchUnservedBuckets(
  args: UnservedBucketsInput,
  signal?: AbortSignal,
): Promise<UnservedBucketsResponse> {
  return apiFetch<UnservedBucketsResponse>('/api/cafeteria/unserved-buckets', {
    query: {
      schoolLocationId: args.schoolLocationId,
      serviceDate: args.serviceDate,
      mealtime: args.mealtime,
    },
    signal,
  })
}

export async function serveAnonymous(input: ServeAnonymousInput): Promise<ServeResponse> {
  return apiFetch<ServeResponse>('/api/cafeteria/serve-anonymous', {
    method: 'POST',
    body: input,
  })
}

export async function serveToStudent(input: ServeToStudentInput): Promise<ServeResponse> {
  return apiFetch<ServeResponse>('/api/cafeteria/serve-to-student', {
    method: 'POST',
    body: input,
  })
}

export async function voidUnassigned(input: VoidUnassignedInput): Promise<ServeResponse> {
  return apiFetch<ServeResponse>('/api/cafeteria/void-unassigned', {
    method: 'POST',
    body: input,
  })
}

/** `POST /api/cafeteria/issues` — lightweight serve-issue audit trace. */
export interface ReportServeIssueInput {
  locationId: string
  serviceDate: string
  mealtime: Mealtime
  studentUserId?: string | null
  studentName: string
  /** Affected order items; empty for a no-order kid (reason-only report). */
  orderItemIds: string[]
  reason: string
}

export async function reportServeIssue(input: ReportServeIssueInput): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>('/api/cafeteria/issues', {
    method: 'POST',
    body: input,
  })
}

export async function allergenCheckForScan(input: AllergenCheckInput): Promise<AllergenCheckResponse> {
  return apiFetch<AllergenCheckResponse>('/api/cafeteria/allergen-check', {
    method: 'POST',
    body: input,
  })
}

export async function commitServedComponents(
  input: CommitServedComponentsInput,
): Promise<CommitServedComponentsResponse> {
  return apiFetch<CommitServedComponentsResponse>('/api/cafeteria/commit-served-components', {
    method: 'POST',
    body: input,
  })
}

export async function fetchCafeteriaLocations(signal?: AbortSignal): Promise<CafeteriaLocationsResponse> {
  return apiFetch<CafeteriaLocationsResponse>('/api/cafeteria/locations', { signal })
}

export async function fetchCafeteriaStudents(
  args: { schoolLocationId: string; serviceDate: string; search?: string },
  signal?: AbortSignal,
): Promise<CafeteriaStudentsResponse> {
  return apiFetch<CafeteriaStudentsResponse>('/api/cafeteria/students', {
    query: args,
    signal,
  })
}

export async function fetchServiceSessions(
  args: { schoolLocationId: string; serviceDate: string },
  signal?: AbortSignal,
): Promise<ServiceSessionsResponse> {
  return apiFetch<ServiceSessionsResponse>('/api/cafeteria/service-sessions', {
    query: args,
    signal,
  })
}

export async function fetchServiceSession(
  args: { schoolLocationId: string; serviceDate: string; mealtime: Mealtime },
  signal?: AbortSignal,
): Promise<CafeteriaServiceSessionResponse> {
  return apiFetch<CafeteriaServiceSessionResponse>('/api/cafeteria/service-session', {
    query: args,
    signal,
  })
}

export async function fetchServiceDayGate(
  args: { schoolLocationId: string; serviceDate: string },
  signal?: AbortSignal,
): Promise<ServiceDayGateResponse> {
  return apiFetch<ServiceDayGateResponse>('/api/cafeteria/service-day-gate', {
    query: args,
    signal,
  })
}

export interface OutstandingServiceDayWire {
  /** yyyy-mm-dd */
  serviceDate: string
  openTasks: Array<{ id: string; subKind: string; title: string; dueAt: string | null }>
  deliveriesRemaining: number
  servicesRemaining: number
}

export interface OutstandingDaysResponse {
  days: OutstandingServiceDayWire[]
  /** True once the count reaches `threshold` — manual ordering is paused. */
  blocked: boolean
  threshold: number
}

export async function fetchOutstandingDays(
  args: { schoolLocationId: string },
  signal?: AbortSignal,
): Promise<OutstandingDaysResponse> {
  return apiFetch<OutstandingDaysResponse>('/api/cafeteria/outstanding-days', {
    query: args,
    signal,
  })
}

export interface ServiceHistoryDayWire {
  /** yyyy-mm-dd */
  serviceDate: string
  hadService: boolean
  /**
   * Source flags per mealtime, from batch originators: `hasParentOrders` ⇒
   * the pass-out sheet holds a named student roster; `hasSchoolOrders` ⇒ it
   * holds anonymous school-forecast tallies. Both ⇒ mixed. The print dialog
   * words its pass-out section from these.
   */
  mealtimes: Array<{ mealtime: Mealtime; complete: boolean; hasParentOrders: boolean; hasSchoolOrders: boolean }>
  deliveries: { total: number; received: number }
  /** DELIVERY-axis dates of unreceived shipments — confirm deep links target these. */
  unreceivedDeliveryDates: string[]
  productionRecords: Array<{
    id: string
    mealtime: Mealtime
    status: string
    signedByName: string | null
    submittedAt: string | null
  }>
  openTaskCount: number
  complete: boolean
}

export interface ServiceHistoryResponse {
  days: ServiceHistoryDayWire[]
  productionRecordsEnabled: boolean
  /**
   * Campus identity + order-deadline policy. Field names match
   * `CafeteriaStaffLocationWire` so `cafeteriaDeadlinePolicy` accepts it
   * unchanged; `timeZone` anchors the print dialog's date presets to the
   * campus clock. All five deadline fields travel together (see the wire
   * comment on `CafeteriaStaffLocationWire`).
   */
  location: Pick<
    CafeteriaStaffLocationWire,
    | 'locationName'
    | 'schoolName'
    | 'timeZone'
    | 'orderDeadlineMode'
    | 'orderDeadlineDayOfWeek'
    | 'orderDeadlineWeeksBefore'
    | 'orderDeadlineTime'
    | 'orderDeadlineRelativeDays'
  > & {
    /**
     * PROD-1234 — the campus serve-grouping dimension; the print dialog
     * starts its roster pills on this axis. Optional: an older server omits
     * it, and the dialog then defaults to grade.
     */
    serveGroupBy?: ServeGroupDimension
    /**
     * PROD-1234 — whether the campus has any homeroom enrollment facts in
     * the range. Gates the "Per homeroom" roster pill. Optional for the same
     * reason; absent reads as false.
     */
    hasHomerooms?: boolean
  }
}

export async function fetchServiceHistory(
  args: { schoolLocationId: string; from: string; to: string },
  signal?: AbortSignal,
): Promise<ServiceHistoryResponse> {
  return apiFetch<ServiceHistoryResponse>('/api/cafeteria/service-history', {
    query: args,
    signal,
  })
}

export async function fetchCafeteriaDeliveries(
  args: { schoolLocationId: string; deliveryDate: string },
  signal?: AbortSignal,
): Promise<CafeteriaDeliveriesResponse> {
  return apiFetch<CafeteriaDeliveriesResponse>('/api/cafeteria/deliveries', {
    query: args,
    signal,
  })
}

export async function completeService(input: CompleteServiceInput): Promise<CompleteServiceResponse> {
  return apiFetch<CompleteServiceResponse>('/api/cafeteria/complete-service', {
    method: 'POST',
    body: input,
  })
}

export interface ReopenServiceInput {
  schoolLocationId: string
  serviceDate: string
  /**
   * Scope the reopen to ONE mealtime. Omit to reopen the whole service day
   * (the Today card's axis). The production-record surface always passes it —
   * reopening a day from one record would un-sign its sibling mealtimes.
   */
  mealtime?: Mealtime
  /**
   * Why the service is being reopened. Reopening CLEARS the signature off the
   * mealtime's production record, and the server records that as a
   * `ProductionRecordAmendment` carrying this reason — which prints on the
   * USDA document. Same convention as an amendment.
   */
  reason?: string
}

/** PROD-1278 — undo a student's serve (all-or-nothing on the server). */
export interface UnserveInput {
  orderItemIds: string[]
  locationId: string
  reason?: string
}

export type UnserveRefusalReason =
  | 'ITEM_VOIDED'
  | 'BILLED'
  | 'SERVICE_COMPLETED'
  | 'POS_SALE'
  | 'NOT_STUDENT_SERVE'
  | 'MONEY_MOVED'

export interface UnserveResponse {
  orderItems: ServeResponse['orderItem'][]
  warnings: string[]
  locationId: string
}

export async function unserve(input: UnserveInput): Promise<UnserveResponse> {
  return apiFetch<UnserveResponse>('/api/cafeteria/unserve', {
    method: 'POST',
    body: input,
  })
}

export async function reopenService(input: ReopenServiceInput): Promise<{ reopened: boolean }> {
  return apiFetch<{ reopened: boolean }>('/api/cafeteria/reopen-service', {
    method: 'POST',
    body: input,
  })
}

export async function receiveDelivery(input: ReceiveDeliveryInput): Promise<ReceiveDeliveryResponse> {
  return apiFetch<ReceiveDeliveryResponse>('/api/cafeteria/receive-delivery', {
    method: 'POST',
    body: input,
  })
}

// ---------------------------------------------------------------------------
// Served-state invalidation
// ---------------------------------------------------------------------------

/**
 * The queries that describe "what has been handed out" at a campus. Every
 * serve/void/tally mutation invalidates exactly these, and PROD-1367's live
 * bump (`hooks/use-cafeteria-live.ts`) replays the same list when ANOTHER
 * tablet on the serving line moves — one list, so a tablet watching a
 * colleague can never re-validate less than the tablet that did the work.
 */
export const SERVED_STATE_QUERY_PREFIXES: readonly (readonly string[])[] = [
  ['cafeteria', 'unserved-buckets'],
  ['cafeteria', 'service-sessions'],
  ['cafeteria', 'service-session'],
  ['cafeteria', 'service-day-gate'],
  ['cafeteria', 'inventory'],
]

/** Invalidate every served-state query. */
export function invalidateServedState(qc: QueryClient): void {
  for (const queryKey of SERVED_STATE_QUERY_PREFIXES) {
    void qc.invalidateQueries({ queryKey: [...queryKey] })
  }
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const unservedBucketsKey = (args: UnservedBucketsInput) => ['cafeteria', 'unserved-buckets', args] as const
export const cafeteriaLocationsKey = ['cafeteria', 'locations'] as const
export const cafeteriaStudentsKey = (args: { schoolLocationId: string; serviceDate: string; search?: string }) =>
  ['cafeteria', 'students', args] as const
export const serviceSessionsKey = (args: { schoolLocationId: string; serviceDate: string }) =>
  ['cafeteria', 'service-sessions', args] as const
export const serviceSessionKey = (args: { schoolLocationId: string; serviceDate: string; mealtime: Mealtime }) =>
  ['cafeteria', 'service-session', args] as const
export const serviceDayGateKey = (args: { schoolLocationId: string; serviceDate: string }) =>
  ['cafeteria', 'service-day-gate', args] as const
export const cafeteriaDeliveriesKey = (args: { schoolLocationId: string; deliveryDate: string }) =>
  ['cafeteria', 'deliveries', args] as const
export const outstandingDaysKey = (args: { schoolLocationId: string }) =>
  ['cafeteria', 'outstanding-days', args] as const
export const serviceHistoryKey = (args: { schoolLocationId: string; from: string; to: string }) =>
  ['cafeteria', 'service-history', args] as const

// ---------------------------------------------------------------------------
// Offline guard — pure helper
// ---------------------------------------------------------------------------

/**
 * `true` when the kiosk is offline. Returns false in SSR / tests
 * where `navigator` is undefined; mutations don't run in those
 * environments anyway. The /cafeteria page also renders an offline
 * banner from the same signal.
 */
export function isOffline(): boolean {
  if (typeof navigator === 'undefined') {
    return false
  }
  return navigator.onLine === false
}

/**
 * Thrown when a mutation tries to fire while the kiosk is offline.
 * The UI catches this and shows a sonner toast — the canonical v1
 * fallback is paper-roster, not a client-side queue.
 */
export class OfflineError extends Error {
  code = 'OFFLINE' as const

  constructor() {
    super('You are offline — serving is disabled. Switch to the paper-fallback runbook.')
    this.name = 'OfflineError'
  }
}

function guardOnline(): void {
  if (isOffline()) {
    throw new OfflineError()
  }
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------
//
// Every date-scoped hook below carries `placeholderData: keepPreviousData`.
// The service/delivery date lives INSIDE the query key, so the cafeteria day
// chevrons mint a brand-new cache entry on every step; without this the whole
// dashboard (header + stepper included) unmounts into a skeleton and the POS
// operator loses the control they just tapped. Pages pair this with an
// `isPending && !data` guard so only the very first load blanks the page.
//
// Note the `enabled: Boolean(args)` + empty-args key fallback: when args go
// null the placeholder latches the last real day's data under the empty key.
// Call sites MUST keep their `if (!locationId) return <Skeleton/>` check ahead
// of any data read — that check does not depend on the query.

export function useUnservedBucketsQuery(args: UnservedBucketsInput | null) {
  return useQuery<UnservedBucketsResponse>({
    enabled: Boolean(args),
    queryKey: unservedBucketsKey(args ?? { schoolLocationId: '', serviceDate: '', mealtime: 'LUNCH' }),
    queryFn: ({ signal }) => fetchUnservedBuckets(args as UnservedBucketsInput, signal),
    placeholderData: keepPreviousData,
    staleTime: 15 * 1000,
    // Refresh on window focus so a worker returning to the tab
    // sees the current bucket counts. 30s polling on top so the
    // grid drifts toward server state even without focus events.
    refetchInterval: 30 * 1000,
    refetchOnWindowFocus: true,
  })
}

export function useCafeteriaLocationsQuery() {
  return useQuery<CafeteriaLocationsResponse>({
    queryKey: cafeteriaLocationsKey,
    queryFn: ({ signal }) => fetchCafeteriaLocations(signal),
    // A worker's campus list changes ~never, and PROD-1302 mounts an observer
    // on this key from every cafeteria surface. Without a staleTime the shared
    // key refires on each route change — on a tablet, on cafeteria wifi.
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * PROD-1302 — the active campus row out of the (argument-free, shared-key)
 * worker-locations list. Null while the list loads, and when the id is not in
 * it: a stale 8h location lock, or a campus this worker may not act at.
 *
 * One definition because the same `find` was hand-rolled in `mode-layout.tsx`
 * and `pages/cafeteria/today.tsx`.
 */
export function pickCafeteriaLocation(
  locations: CafeteriaStaffLocationWire[] | undefined,
  locationId: string | null | undefined,
): CafeteriaStaffLocationWire | null {
  if (!locations || !locationId) {
    return null
  }
  return locations.find((l) => l.locationId === locationId) ?? null
}

/**
 * Record that a student's name is written family-name-first (PROD-1107).
 * Invalidates every cafeteria query: the server composes the display name,
 * so the serve list, the printed roster and the CSV only change on re-fetch.
 */
export function useSetStudentNameOrderMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: {
      studentUserId: string
      schoolLocationId: string
      serviceDate: string
      familyNameFirst: boolean
    }) =>
      apiFetch<{ studentUserId: string; familyNameFirst: boolean }>(
        `/api/cafeteria/students/${encodeURIComponent(vars.studentUserId)}/name-order`,
        {
          method: 'PATCH',
          body: {
            schoolLocationId: vars.schoolLocationId,
            serviceDate: vars.serviceDate,
            familyNameFirst: vars.familyNameFirst,
          },
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cafeteria'] })
    },
  })
}

export interface SetStudentGradeResponse {
  studentUserId: string
  grade: string
  gradeRangeCode: string | null
}

/**
 * Assign a student's grade from the serve line. A student whose grade maps
 * to no USDA band sees an empty register (the menu grade gate fails closed),
 * so the worker fixes the roster on the spot. Invalidates every cafeteria
 * query: the roster header, the register context + menu, and the pool
 * buckets all read the band.
 */
export function useSetStudentGradeMutation() {
  const qc = useQueryClient()
  return useMutation<
    SetStudentGradeResponse,
    ApiError,
    { studentUserId: string; schoolLocationId: string; grade: string }
  >({
    mutationFn: (vars) =>
      apiFetch<SetStudentGradeResponse>(`/api/cafeteria/students/${encodeURIComponent(vars.studentUserId)}/grade`, {
        method: 'PUT',
        body: { schoolLocationId: vars.schoolLocationId, grade: vars.grade },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cafeteria'] })
    },
  })
}

export function useCafeteriaStudentsQuery(
  args: { schoolLocationId: string; serviceDate: string; search?: string } | null,
) {
  return useQuery<CafeteriaStudentsResponse>({
    enabled: Boolean(args),
    queryKey: cafeteriaStudentsKey(args ?? { schoolLocationId: '', serviceDate: '', search: '' }),
    queryFn: ({ signal }) =>
      fetchCafeteriaStudents(args as { schoolLocationId: string; serviceDate: string; search?: string }, signal),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  })
}

export function useServiceSessionsQuery(args: { schoolLocationId: string; serviceDate: string } | null) {
  return useQuery<ServiceSessionsResponse>({
    enabled: Boolean(args),
    queryKey: serviceSessionsKey(args ?? { schoolLocationId: '', serviceDate: '' }),
    queryFn: ({ signal }) => fetchServiceSessions(args as { schoolLocationId: string; serviceDate: string }, signal),
    placeholderData: keepPreviousData,
    staleTime: 15 * 1000,
    refetchInterval: 30 * 1000,
  })
}

export function useServiceSessionQuery(
  args: { schoolLocationId: string; serviceDate: string; mealtime: Mealtime } | null,
) {
  return useQuery<CafeteriaServiceSessionResponse>({
    enabled: Boolean(args),
    queryKey: serviceSessionKey(args ?? { schoolLocationId: '', serviceDate: '', mealtime: 'LUNCH' }),
    queryFn: ({ signal }) =>
      fetchServiceSession(args as { schoolLocationId: string; serviceDate: string; mealtime: Mealtime }, signal),
    placeholderData: keepPreviousData,
    staleTime: 10 * 1000,
    refetchInterval: 15 * 1000,
  })
}

export function useServiceDayGateQuery(args: { schoolLocationId: string; serviceDate: string } | null) {
  return useQuery<ServiceDayGateResponse>({
    enabled: Boolean(args),
    queryKey: serviceDayGateKey(args ?? { schoolLocationId: '', serviceDate: '' }),
    queryFn: ({ signal }) => fetchServiceDayGate(args as { schoolLocationId: string; serviceDate: string }, signal),
    placeholderData: keepPreviousData,
    staleTime: 10 * 1000,
  })
}

export function useCafeteriaDeliveriesQuery(args: { schoolLocationId: string; deliveryDate: string } | null) {
  return useQuery<CafeteriaDeliveriesResponse>({
    enabled: Boolean(args),
    queryKey: cafeteriaDeliveriesKey(args ?? { schoolLocationId: '', deliveryDate: '' }),
    queryFn: ({ signal }) =>
      fetchCafeteriaDeliveries(args as { schoolLocationId: string; deliveryDate: string }, signal),
    placeholderData: keepPreviousData,
    staleTime: 10 * 1000,
    refetchInterval: 30 * 1000,
  })
}

export function useServiceHistoryQuery(args: { schoolLocationId: string; from: string; to: string } | null) {
  return useQuery<ServiceHistoryResponse>({
    enabled: Boolean(args),
    queryKey: serviceHistoryKey(args ?? { schoolLocationId: '', from: '', to: '' }),
    queryFn: ({ signal }) =>
      fetchServiceHistory(args as { schoolLocationId: string; from: string; to: string }, signal),
    // The History page widens `from` after the outstanding-days rollup loads
    // (and on "Show older days"); keep the current list rendered instead of
    // flashing the skeleton on the refetch.
    placeholderData: keepPreviousData,
    staleTime: 30 * 1000,
  })
}

export function useOutstandingDaysQuery(args: { schoolLocationId: string } | null) {
  return useQuery<OutstandingDaysResponse>({
    enabled: Boolean(args),
    queryKey: outstandingDaysKey(args ?? { schoolLocationId: '' }),
    queryFn: ({ signal }) => fetchOutstandingDays(args as { schoolLocationId: string }, signal),
    staleTime: 30 * 1000,
  })
}

// ---------------------------------------------------------------------------
// Offline-aware mutation hooks (Plan 23)
// ---------------------------------------------------------------------------
//
// Three cafeteria mutations are queue-on-failure: when the kiosk is
// offline (or the network call fails on a flaky cafeteria WiFi), the
// hook enqueues a SyncQueue row + resolves with a synthesized
// optimistic ServeResponse so the UI's served-chip rail can proceed
// without waiting for the server. Plan 24's replay manager picks the
// row off the queue when network returns.
//
// The synthesized response is intentionally minimal: the page uses
// `orderItemId` + an `OFFLINE_QUEUED` marker on `status` so any
// downstream UI that branches on the status field can detect the
// queued state. Plan 24 replaces it with the real server response
// once replay succeeds.

export const OFFLINE_QUEUED_STATUS = 'OFFLINE_QUEUED' as const

function synthesizeOfflineServeResponse(orderItemId: string, locationId: string): ServeResponse {
  return {
    orderItem: {
      id: orderItemId,
      status: OFFLINE_QUEUED_STATUS,
      assignedToUserId: null,
      servedAt: new Date().toISOString(),
      servedByUserId: null,
      billedToAccountId: null,
      menuItemId: null,
      batchId: null,
    },
    warnings: ['queued-offline'],
    locationId,
  }
}

/**
 * Did this fetch fail in a way that maps to "kiosk is offline"? A
 * TypeError on `fetch()` means the network layer rejected — that's
 * the canonical offline signal. ApiError (HTTP 4xx/5xx) is a real
 * server response and should NOT be queued.
 *
 * Exported for the POS sale hook (`api/cafeteria-pos.ts`, PR6), which
 * shares the queue-on-failure shape.
 */
export function isOfflineFetchError(err: unknown): boolean {
  if (err instanceof ApiError) {
    return false
  }
  if (err instanceof OfflineError) {
    return true
  }
  if (err instanceof TypeError) {
    return true
  }
  return false
}

/**
 * Ensure an `idempotencyKey` is set on the input. Returns a new object
 * whose key is either the caller-supplied value or a freshly-generated
 * UUID — never mutates the caller's object.
 *
 * Pure — exported for tests.
 */
export function withIdempotencyKey<T extends object>(input: T): T & { idempotencyKey: string } {
  const existing = (input as { idempotencyKey?: unknown }).idempotencyKey
  if (typeof existing === 'string' && existing.length > 0) {
    return input as T & { idempotencyKey: string }
  }
  return { ...input, idempotencyKey: generateQueueId() }
}

export function useServeAnonymousMutation() {
  const qc = useQueryClient()
  return useMutation<ServeResponse, ApiError, ServeAnonymousInput>({
    mutationFn: async (rawInput) => {
      const input = withIdempotencyKey(rawInput)
      if (isOffline()) {
        await enqueueMutation({
          operation: 'serveAnonymous',
          variables: input as unknown as Record<string, unknown>,
          optimisticPatch: { orderItemId: input.orderItemId, served: true },
          id: input.idempotencyKey,
        })
        return synthesizeOfflineServeResponse(input.orderItemId, input.locationId)
      }
      try {
        return await serveAnonymous(input)
      } catch (err) {
        if (isOfflineFetchError(err)) {
          await enqueueMutation({
            operation: 'serveAnonymous',
            variables: input as unknown as Record<string, unknown>,
            optimisticPatch: { orderItemId: input.orderItemId, served: true },
            id: input.idempotencyKey,
          })
          return synthesizeOfflineServeResponse(input.orderItemId, input.locationId)
        }
        throw err
      }
    },
    onSuccess: () => {
      invalidateServedState(qc)
    },
  })
}

export function useServeToStudentMutation() {
  const qc = useQueryClient()
  return useMutation<ServeResponse, ApiError, ServeToStudentInput>({
    mutationFn: async (rawInput) => {
      const input = withIdempotencyKey(rawInput)
      if (isOffline()) {
        await enqueueMutation({
          operation: 'serveToStudent',
          variables: input as unknown as Record<string, unknown>,
          optimisticPatch: { orderItemId: input.orderItemId, studentUserId: input.studentUserId, served: true },
          id: input.idempotencyKey,
        })
        const synth = synthesizeOfflineServeResponse(input.orderItemId, input.locationId)
        synth.orderItem.assignedToUserId = input.studentUserId
        return synth
      }
      try {
        return await serveToStudent(input)
      } catch (err) {
        if (isOfflineFetchError(err)) {
          await enqueueMutation({
            operation: 'serveToStudent',
            variables: input as unknown as Record<string, unknown>,
            optimisticPatch: { orderItemId: input.orderItemId, studentUserId: input.studentUserId, served: true },
            id: input.idempotencyKey,
          })
          const synth = synthesizeOfflineServeResponse(input.orderItemId, input.locationId)
          synth.orderItem.assignedToUserId = input.studentUserId
          return synth
        }
        throw err
      }
    },
    onSuccess: () => {
      invalidateServedState(qc)
    },
  })
}

export function useVoidUnassignedMutation() {
  const qc = useQueryClient()
  return useMutation<ServeResponse, ApiError, VoidUnassignedInput>({
    mutationFn: async (rawInput) => {
      const input = withIdempotencyKey(rawInput)
      if (isOffline()) {
        await enqueueMutation({
          operation: 'voidUnassigned',
          variables: input as unknown as Record<string, unknown>,
          optimisticPatch: { orderItemId: input.orderItemId, voided: true },
          id: input.idempotencyKey,
        })
        return synthesizeOfflineServeResponse(input.orderItemId, input.locationId)
      }
      try {
        return await voidUnassigned(input)
      } catch (err) {
        if (isOfflineFetchError(err)) {
          await enqueueMutation({
            operation: 'voidUnassigned',
            variables: input as unknown as Record<string, unknown>,
            optimisticPatch: { orderItemId: input.orderItemId, voided: true },
            id: input.idempotencyKey,
          })
          return synthesizeOfflineServeResponse(input.orderItemId, input.locationId)
        }
        throw err
      }
    },
    onSuccess: () => {
      invalidateServedState(qc)
    },
  })
}

/**
 * PROD-1278 — undo a student's serve. ONLINE-ONLY by design: a queued undo
 * would replay FIFO behind queued sales/completions and mostly land as
 * `UNDO_NOT_ALLOWED`; undoing a serve that is itself still queued is a
 * client-side queue removal, not a server call.
 */
export function useUnserveMutation() {
  const qc = useQueryClient()
  return useMutation<UnserveResponse, ApiError | OfflineError, UnserveInput>({
    mutationFn: async (input) => {
      guardOnline()
      return unserve(input)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'unserved-buckets'] })
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'service-sessions'] })
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'service-session'] })
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'inventory'] })
    },
  })
}

export function useAllergenCheckMutation() {
  return useMutation<AllergenCheckResponse, ApiError | OfflineError, AllergenCheckInput>({
    mutationFn: async (input) => {
      guardOnline()
      return allergenCheckForScan(input)
    },
  })
}

export function useCommitServedComponentsMutation() {
  return useMutation<CommitServedComponentsResponse, ApiError | OfflineError, CommitServedComponentsInput>({
    mutationFn: async (input) => {
      guardOnline()
      return commitServedComponents(input)
    },
  })
}

export function useCompleteServiceMutation() {
  const qc = useQueryClient()
  return useMutation<CompleteServiceResponse, ApiError | OfflineError, CompleteServiceInput>({
    mutationFn: async (input) => {
      guardOnline()
      return completeService(input)
    },
    onSuccess: () => {
      invalidateServedState(qc)
      // Completing a mealtime can flip the school week view's History badge
      // (PROD-1123 v2) — refresh its month flags + day details.
      void qc.invalidateQueries({ queryKey: ['school-orders', 'service-summary'] })
      void qc.invalidateQueries({ queryKey: ['school-orders', 'service-day'] })
    },
  })
}

export function useReopenServiceMutation() {
  const qc = useQueryClient()
  return useMutation<{ reopened: boolean }, ApiError | OfflineError, ReopenServiceInput>({
    mutationFn: async (input) => {
      guardOnline()
      return reopenService(input)
    },
    onSuccess: () => {
      invalidateServedState(qc)
      // Reopening puts the day back into the outstanding count + history rows.
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'outstanding-days'] })
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'service-history'] })
      // ...and pulls the school week view's "Service complete" badge back
      // (PROD-1123 v2).
      void qc.invalidateQueries({ queryKey: ['school-orders', 'service-summary'] })
      void qc.invalidateQueries({ queryKey: ['school-orders', 'service-day'] })
    },
  })
}

export function useReceiveDeliveryMutation() {
  const qc = useQueryClient()
  return useMutation<ReceiveDeliveryResponse, ApiError | OfflineError, ReceiveDeliveryInput>({
    mutationFn: async (input) => {
      guardOnline()
      return receiveDelivery(input)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'deliveries'] })
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'service-day-gate'] })
      // Receiving pre-fills the Serve surface's temperatures — refresh the
      // sessions so a receipt made from Production Records shows up there.
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'service-sessions'] })
      // Confirming a (possibly past) delivery closes its confirm-received
      // task — refresh the outstanding count + history rows.
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'outstanding-days'] })
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'service-history'] })
    },
  })
}

// ---------------------------------------------------------------------------
// LocationLockGate persistence — exported pure helpers
// ---------------------------------------------------------------------------

export const LOCATION_LOCK_STORAGE_KEY = 'cafeteriaLocationLock'
export const LOCATION_LOCK_TTL_MS = 8 * 60 * 60 * 1000 // 8 hours

export interface CafeteriaLocationLock {
  locationId: string
  /** Epoch ms when the lock was written. */
  writtenAt: number
}

const locationLockListeners = new Set<() => void>()

/**
 * Subscribe to in-process changes of the persisted lock — every write and
 * clear made through the helpers below. `useSyncExternalStore`-shaped (returns
 * the unsubscribe) so `useCafeteriaLocationSources` can read the lock LIVE:
 * the mode layout clears a lock naming a campus the worker is not permitted
 * at (PROD-1302), and that clear has to reach the hook in the same tab, or
 * the location gate would never open. The hook used to memoize the lock once
 * at mount, which is exactly how a foreign lock stayed the resolved campus.
 */
export function subscribeCafeteriaLocationLock(listener: () => void): () => void {
  locationLockListeners.add(listener)
  return () => {
    locationLockListeners.delete(listener)
  }
}

function notifyCafeteriaLocationLockChanged(): void {
  for (const listener of locationLockListeners) {
    listener()
  }
}

/**
 * Read the persisted location lock. Returns null when no lock is
 * set, when the lock is expired (older than 8 hours), or when
 * localStorage is unavailable.
 *
 * Pure-ish: the `now` arg defaults to `Date.now()` so tests can
 * advance the clock without mocking globals.
 */
export function readCafeteriaLocationLock(now: number = Date.now()): CafeteriaLocationLock | null {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return null
  }
  try {
    const raw = localStorage.getItem(LOCATION_LOCK_STORAGE_KEY)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw) as Partial<CafeteriaLocationLock>
    if (typeof parsed.locationId !== 'string' || typeof parsed.writtenAt !== 'number') {
      return null
    }
    if (now - parsed.writtenAt > LOCATION_LOCK_TTL_MS) {
      return null
    }
    return { locationId: parsed.locationId, writtenAt: parsed.writtenAt }
  } catch {
    return null
  }
}

export function writeCafeteriaLocationLock(locationId: string, now: number = Date.now()): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return
  }
  try {
    const payload: CafeteriaLocationLock = { locationId, writtenAt: now }
    localStorage.setItem(LOCATION_LOCK_STORAGE_KEY, JSON.stringify(payload))
    notifyCafeteriaLocationLockChanged()
  } catch {
    // Quota or disabled — silently degrade; the URL param remains as
    // the secondary source of truth.
  }
}

export function clearCafeteriaLocationLock(): void {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
    return
  }
  try {
    localStorage.removeItem(LOCATION_LOCK_STORAGE_KEY)
    notifyCafeteriaLocationLockChanged()
  } catch {
    // ignore
  }
}

// ---------------------------------------------------------------------------
// Quantity-stepper batch serve (students-unknown mode)
// ---------------------------------------------------------------------------

export interface ServeAnonymousBatchInput {
  schoolLocationId: string
  serviceDate: string
  mealtime: Mealtime
  menuItemId: string
  /** Portion tier (OrderItem.gradeRangeId) bucket filter. */
  gradeRangeId?: string | null
  /** `(groupDimension, groupValue)` bucket filter — both or neither. */
  groupDimension?: string | null
  groupValue?: string | null
  quantity: number
  idempotencyKey?: string
}

export interface ServeAnonymousBatchResponse {
  servedCount: number
  requestedCount: number
  orderItemIds: string[]
  warnings: string[]
}

export async function serveAnonymousBatch(input: ServeAnonymousBatchInput): Promise<ServeAnonymousBatchResponse> {
  return apiFetch<ServeAnonymousBatchResponse>('/api/cafeteria/serve-anonymous-batch', {
    method: 'POST',
    body: input,
  })
}

// ---------------------------------------------------------------------------
// PROD-1278 — school tally taps (immediate, shared, offline-aware)
// ---------------------------------------------------------------------------

export interface ServeTallyDeltaInput {
  schoolLocationId: string
  /** ISO date (YYYY-MM-DD). */
  serviceDate: string
  mealtime: Mealtime
  bundleDeliverableId: string | null
  menuItemId: string | null
  gradeRangeId: string | null
  groupDimension: string | null
  groupValue: string | null
  /** +N / −N servings; the server floors the bucket at 0. */
  delta: number
  idempotencyKey?: string
  /**
   * Client-only: the tally row this tap belongs to, snapshotted onto the
   * queue row so a queued tap keeps showing on the right row offline.
   */
  rowKey?: string
}

export interface ServeTallyDeltaResponse {
  servedQty: number
  appliedDelta: number
  replayed: boolean
  /** Client-only: the tap was queued offline (the sync-queue row now carries it). */
  queued?: boolean
}

export async function serveTallyDelta(input: ServeTallyDeltaInput): Promise<ServeTallyDeltaResponse> {
  const { rowKey: _rowKey, ...body } = input
  return apiFetch<ServeTallyDeltaResponse>('/api/cafeteria/tally', { method: 'POST', body })
}

/**
 * One tally tap. Offline-aware like the batch serve: enqueues a
 * `serveTallyDelta` op (with the row key on its patch) and resolves with a
 * synthesized response so the count advances; the replay manager drains it
 * on reconnect with the same idempotency key.
 */
export function useServeTallyDeltaMutation() {
  const qc = useQueryClient()
  return useMutation<ServeTallyDeltaResponse, ApiError, ServeTallyDeltaInput>({
    mutationFn: async (rawInput) => {
      const input = withIdempotencyKey(rawInput)
      const { rowKey, ...variables } = input
      const synth = (): ServeTallyDeltaResponse => ({
        servedQty: 0,
        appliedDelta: input.delta,
        replayed: false,
        queued: true,
      })
      const enqueue = () =>
        enqueueMutation({
          operation: 'serveTallyDelta',
          variables: variables as unknown as Record<string, unknown>,
          optimisticPatch: { rowKey: rowKey ?? null, delta: input.delta },
          id: input.idempotencyKey,
        })
      if (isOffline()) {
        await enqueue()
        return synth()
      }
      try {
        return await serveTallyDelta(input)
      } catch (err) {
        if (isOfflineFetchError(err)) {
          await enqueue()
          return synth()
        }
        throw err
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'service-session'] })
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'service-sessions'] })
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'inventory'] })
    },
  })
}

/**
 * Serve N anonymous servings of one item in a single idempotent call.
 * Offline-aware: enqueues a `serveAnonymousBatch` op + resolves with a
 * synthesized response so the stepper advances; the replay manager
 * drains it on reconnect.
 */
export function useServeAnonymousBatchMutation() {
  const qc = useQueryClient()
  return useMutation<ServeAnonymousBatchResponse, ApiError, ServeAnonymousBatchInput>({
    mutationFn: async (rawInput) => {
      const input = withIdempotencyKey(rawInput)
      const synth = (): ServeAnonymousBatchResponse => ({
        servedCount: input.quantity,
        requestedCount: input.quantity,
        orderItemIds: [],
        warnings: ['queued-offline'],
      })
      if (isOffline()) {
        await enqueueMutation({
          operation: 'serveAnonymousBatch',
          variables: input as unknown as Record<string, unknown>,
          optimisticPatch: { menuItemId: input.menuItemId, quantity: input.quantity },
          id: input.idempotencyKey,
        })
        return synth()
      }
      try {
        return await serveAnonymousBatch(input)
      } catch (err) {
        if (isOfflineFetchError(err)) {
          await enqueueMutation({
            operation: 'serveAnonymousBatch',
            variables: input as unknown as Record<string, unknown>,
            optimisticPatch: { menuItemId: input.menuItemId, quantity: input.quantity },
            id: input.idempotencyKey,
          })
          return synth()
        }
        throw err
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'service-session'] })
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'service-sessions'] })
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'unserved-buckets'] })
      void qc.invalidateQueries({ queryKey: ['cafeteria', 'inventory'] })
    },
  })
}
